import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  FormGroup,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DataGrid } from "@mui/x-data-grid";
import {
  getDrivers,
  getDriverAccess,
  updateDriverAccess,
  getDistricts,
  getDriverActiveDeliveries,
  reassignDeliveries,
  getTransferHistory,
} from "../../api/deliveryCompanyService";
import { DeliveryStatusLabels, DeliveryStatusColors } from "../Admin/constants";

const COUNTRY_PAIRS = [
  { key: "IL-IL", originCountry: "IL", destinationCountry: "IL", label: "IL → IL" },
  { key: "IL-PS", originCountry: "IL", destinationCountry: "PS", label: "IL → PS" },
  { key: "PS-IL", originCountry: "PS", destinationCountry: "IL", label: "PS → IL" },
  { key: "PS-PS", originCountry: "PS", destinationCountry: "PS", label: "PS → PS" },
];

export default function DriverAccess() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);

  function loadList() {
    setLoading(true);
    getDrivers()
      .then((data) => {
        setRows(data);
        setError("");
      })
      .catch(() => setError("Could not load drivers. Is the API running?"))
      .finally(() => setLoading(false));
  }

  useEffect(loadList, []);

  async function openDetail(userId) {
    const data = await getDriverAccess(userId);
    setDetail(data);
  }

  const columns = useMemo(
    () => [
      { field: "fullName", headerName: "Name", flex: 1, minWidth: 180 },
      { field: "phone", headerName: "Phone", width: 140, valueGetter: (v) => v || "-" },
      {
        field: "isAvailable",
        headerName: "Available",
        width: 120,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value ? "Available" : "Offline"}
            color={params.value ? "success" : "default"}
          />
        ),
      },
      {
        field: "hasFullAccess",
        headerName: "Access",
        width: 160,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value ? "Full access" : "Restricted"}
            color={params.value ? "success" : "warning"}
          />
        ),
      },
    ],
    []
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Driver Access
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.userId}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          onRowClick={(params) => openDetail(params.row.userId)}
          sx={{ border: "none" }}
        />
      </Box>

      <MoveOrdersSection drivers={rows} />

      <Drawer anchor="right" open={Boolean(detail)} onClose={() => setDetail(null)}>
        {detail && (
          <Box sx={{ width: 420, p: 3 }}>
            <Typography variant="h6">{detail.fullName}</Typography>

            <CompanyScopeSummary detail={detail} />

            <AccessForm
              detail={detail}
              onSaved={(updated) => {
                setDetail(updated);
                loadList();
              }}
            />
          </Box>
        )}
      </Drawer>
    </Box>
  );
}

function CompanyScopeSummary({ detail }) {
  return (
    <Box sx={{ my: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Your company's access
      </Typography>
      {detail.companyHasFullAccess ? (
        <Typography variant="body2">Full access - you can grant a driver any pickup/dropoff area.</Typography>
      ) : (
        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
          {detail.companyAllowedPairs.length > 0 && (
            <Typography variant="body2">
              Pairs: {detail.companyAllowedPairs.map((p) => `${p.originCountry}→${p.destinationCountry}`).join(", ")}
            </Typography>
          )}
          {detail.companyAllowedDistricts.length > 0 && (
            <Typography variant="body2">Districts: {detail.companyAllowedDistricts.join(", ")}</Typography>
          )}
          {detail.companyAllowedPairs.length === 0 && detail.companyAllowedDistricts.length === 0 && (
            <Typography variant="body2">No areas granted yet - contact the admin.</Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}

function AccessForm({ detail, onSaved }) {
  const [mode, setMode] = useState(detail.hasFullAccess ? "full" : "restricted");
  const [selected, setSelected] = useState(
    new Set(detail.allowedPairs.map((p) => `${p.originCountry}-${p.destinationCountry}`))
  );
  const [districts, setDistricts] = useState(detail.allowedDistricts ?? []);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [districtsLoading, setDistrictsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const availablePairs = detail.companyHasFullAccess
    ? COUNTRY_PAIRS
    : COUNTRY_PAIRS.filter((p) =>
        detail.companyAllowedPairs.some(
          (cp) => cp.originCountry === p.originCountry && cp.destinationCountry === p.destinationCountry
        )
      );

  useEffect(() => {
    if (detail.companyHasFullAccess) {
      getDistricts()
        .then(setDistrictOptions)
        .catch(() => setDistrictOptions([]))
        .finally(() => setDistrictsLoading(false));
    } else {
      setDistrictOptions(detail.companyAllowedDistricts ?? []);
      setDistrictsLoading(false);
    }
  }, [detail.companyHasFullAccess, detail.companyAllowedDistricts]);

  function togglePair(key) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  async function handleSave() {
    setError("");
    if (mode === "restricted" && selected.size === 0 && districts.length === 0) {
      setError("Select at least one country pair or district, or choose Full Access.");
      return;
    }
    setSaving(true);
    try {
      const pairs = COUNTRY_PAIRS.filter((p) => selected.has(p.key)).map(
        ({ originCountry, destinationCountry }) => ({ originCountry, destinationCountry })
      );
      const updated = await updateDriverAccess(
        detail.userId,
        mode === "full",
        pairs,
        mode === "restricted" ? districts : []
      );
      onSaved(updated);
    } catch {
      setError("Could not save access settings.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
        <FormControlLabel value="full" control={<Radio />} label="Full access (same as company)" />
        <FormControlLabel value="restricted" control={<Radio />} label="Restricted to specific pairs / districts" />
      </RadioGroup>

      {mode === "restricted" && (
        <>
          <Typography variant="subtitle2">Country pairs</Typography>
          <FormGroup sx={{ pl: 2 }}>
            {availablePairs.map((p) => (
              <FormControlLabel
                key={p.key}
                control={<Checkbox checked={selected.has(p.key)} onChange={() => togglePair(p.key)} />}
                label={p.label}
              />
            ))}
            {availablePairs.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Your company hasn't been granted any country pairs.
              </Typography>
            )}
          </FormGroup>

          <Typography variant="subtitle2">Districts</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
            Only districts your company itself is allowed to see can be granted to a driver.
          </Typography>
          <Autocomplete
            multiple
            options={districtOptions}
            loading={districtsLoading}
            value={districts}
            onChange={(e, value) => setDistricts(value)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip size="small" label={option} {...getTagProps({ index })} key={option} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} placeholder={districts.length ? "" : "Select districts"} size="small" />
            )}
          />
        </>
      )}

      <Button variant="contained" disabled={saving} onClick={handleSave}>
        Save Access Settings
      </Button>
    </Stack>
  );
}

function groupByCity(deliveries, sortOrder) {
  const groups = new Map();
  for (const delivery of deliveries) {
    const city = delivery.dropoffCity || "Unknown city";
    if (!groups.has(city)) groups.set(city, []);
    groups.get(city).push(delivery);
  }
  const dir = sortOrder === "newest" ? -1 : 1;
  const byOrderDate = (a, b) => dir * (new Date(a.orderCreatedAt) - new Date(b.orderCreatedAt));

  return [...groups.entries()]
    .map(([city, items]) => ({ city, items: [...items].sort(byOrderDate) }))
    .sort((a, b) => byOrderDate(a.items[0], b.items[0]));
}

// Oldest orders are the most urgent to deliver first (first-come-first-served), so this flags
// how long an order has been waiting rather than just showing a raw timestamp.
function describeWait(orderCreatedAt) {
  const hours = (Date.now() - new Date(orderCreatedAt).getTime()) / 36e5;
  const label = hours < 1 ? "<1h ago" : `${Math.floor(hours)}h ago`;
  const color = hours >= 6 ? "error" : hours >= 2 ? "warning" : "default";
  return { label, color };
}

const MOVABLE_STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: 1, label: DeliveryStatusLabels[1] },
  { value: 2, label: DeliveryStatusLabels[2] },
  { value: 3, label: DeliveryStatusLabels[3] },
];

function MoveOrdersSection({ drivers }) {
  const [fromDriverId, setFromDriverId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [sortOrder, setSortOrder] = useState("oldest");
  const [deliveries, setDeliveries] = useState([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState("");
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  const fromDriver = drivers.find((d) => d.userId === fromDriverId) || null;

  function loadDeliveries(driverId, status) {
    setSelected(new Set());
    if (!driverId) {
      setDeliveries([]);
      return;
    }
    setLoadingDeliveries(true);
    getDriverActiveDeliveries(driverId, status || undefined)
      .then((data) => setDeliveries(data.items || []))
      .catch(() => setDeliveries([]))
      .finally(() => setLoadingDeliveries(false));
  }

  useEffect(() => {
    loadDeliveries(fromDriverId, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDriverId, statusFilter]);

  const groups = useMemo(() => groupByCity(deliveries, sortOrder), [deliveries, sortOrder]);

  function toggleOne(deliveryId) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(deliveryId)) next.delete(deliveryId);
      else next.add(deliveryId);
      return next;
    });
  }

  function toggleGroup(items) {
    const ids = items.map((i) => i.deliveryId);
    const allSelected = ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  }

  function handleMoved(count, driverName) {
    setSnackbar(`Moved ${count} order${count === 1 ? "" : "s"} to ${driverName}.`);
    setMoveDialogOpen(false);
    setHistoryRefreshKey((k) => k + 1);
    loadDeliveries(fromDriverId, statusFilter);
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Move Orders Between Drivers
      </Typography>

      <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">
        <Box sx={{ flex: 2, minWidth: 0 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mb: 2 }}>
            <Autocomplete
              options={drivers}
              getOptionLabel={(d) => d.fullName}
              isOptionEqualToValue={(a, b) => a.userId === b.userId}
              value={fromDriver}
              onChange={(e, value) => setFromDriverId(value ? value.userId : null)}
              renderInput={(params) => (
                <TextField {...params} label="Driver" placeholder="Select a driver to view their orders" size="small" />
              )}
              sx={{ width: 260 }}
            />

            <ToggleButtonGroup
              value={statusFilter}
              exclusive
              size="small"
              onChange={(e, value) => {
                if (value !== null) setStatusFilter(value);
              }}
            >
              {MOVABLE_STATUS_FILTERS.map((option) => (
                <ToggleButton key={option.label} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <Select size="small" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} sx={{ minWidth: 190 }}>
              <MenuItem value="oldest">Oldest ordered first</MenuItem>
              <MenuItem value="newest">Newest ordered first</MenuItem>
            </Select>
          </Box>

          {!fromDriverId && (
            <Alert severity="info">Select a driver to see their orders that haven't been delivered yet.</Alert>
          )}

          {fromDriverId && !loadingDeliveries && deliveries.length === 0 && (
            <Alert severity="info">
              {statusFilter
                ? `This driver has no ${DeliveryStatusLabels[statusFilter].toLowerCase()} orders.`
                : "This driver has no active (not yet delivered) orders."}
            </Alert>
          )}

          {fromDriverId && groups.length > 0 && (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {selected.size} of {deliveries.length} orders selected
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  disabled={selected.size === 0}
                  onClick={() => setMoveDialogOpen(true)}
                >
                  Move to driver
                </Button>
              </Stack>

              {groups.map(({ city, items }, groupIndex) => {
                const ids = items.map((i) => i.deliveryId);
                const allSelected = ids.every((id) => selected.has(id));
                const someSelected = !allSelected && ids.some((id) => selected.has(id));
                return (
                  <Accordion key={city} defaultExpanded={groupIndex === 0 || groups.length <= 3}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleGroup(items)}
                        />
                        <Typography sx={{ flex: 1 }}>{city}</Typography>
                        <Chip size="small" label={`${items.length} order${items.length === 1 ? "" : "s"}`} />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <List dense disablePadding>
                        {items.map((delivery, index) => {
                          const wait = describeWait(delivery.orderCreatedAt);
                          return (
                            <ListItemButton
                              key={delivery.deliveryId}
                              onClick={() => toggleOne(delivery.deliveryId)}
                              sx={{ borderRadius: 1 }}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Checkbox
                                  edge="start"
                                  checked={selected.has(delivery.deliveryId)}
                                  tabIndex={-1}
                                  disableRipple
                                />
                              </ListItemIcon>
                              {sortOrder === "oldest" && groupIndex === 0 && index === 0 && (
                                <Chip size="small" label="Next up" color="error" variant="outlined" sx={{ mr: 1 }} />
                              )}
                              <ListItemText
                                primary={`#${delivery.orderNumber} — ${delivery.customerName}`}
                                secondary={`${delivery.dropoffStreet || ""}${
                                  delivery.dropoffStreet ? ", " : ""
                                }${delivery.dropoffCity || ""}`}
                              />
                              <Chip size="small" label={wait.label} color={wait.color} sx={{ ml: 1 }} />
                              <Chip
                                size="small"
                                label={DeliveryStatusLabels[delivery.status] || delivery.status}
                                color={DeliveryStatusColors[delivery.status] || "default"}
                                sx={{ ml: 1 }}
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 280, width: { xs: "100%", md: "auto" } }}>
          <TransferHistory refreshKey={historyRefreshKey} />
        </Box>
      </Stack>

      {moveDialogOpen && (
        <MoveOrdersDialog
          open={moveDialogOpen}
          onClose={() => setMoveDialogOpen(false)}
          drivers={drivers.filter((d) => d.userId !== fromDriverId)}
          deliveryIds={[...selected]}
          onMoved={handleMoved}
        />
      )}

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar("")}
        message={snackbar}
      />
    </Box>
  );
}

function MoveOrdersDialog({ open, onClose, drivers, deliveryIds, onMoved }) {
  const [toDriver, setToDriver] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleConfirm() {
    if (!toDriver) {
      setError("Choose a driver to move these orders to.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await reassignDeliveries(deliveryIds, toDriver.userId, reason || undefined);
      onMoved(deliveryIds.length, toDriver.fullName);
    } catch {
      setError("Could not move these orders. Please refresh and try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Move {deliveryIds.length} order{deliveryIds.length === 1 ? "" : "s"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Autocomplete
            options={drivers}
            getOptionLabel={(d) => d.fullName}
            isOptionEqualToValue={(a, b) => a.userId === b.userId}
            value={toDriver}
            onChange={(e, value) => setToDriver(value)}
            renderInput={(params) => <TextField {...params} label="Move to driver" size="small" autoFocus />}
          />
          <TextField
            label="Reason (optional)"
            placeholder="e.g. Handoff at warehouse"
            size="small"
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={saving}>
          Move
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TransferHistory({ refreshKey }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getTransferHistory({ page: 1, pageSize: 10 })
      .then((data) => setLogs(data.items || []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  return (
    <Box sx={{ bgcolor: "background.paper", borderRadius: 2, p: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Recent transfers
      </Typography>
      <Divider sx={{ mb: 1 }} />
      {loading && (
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      )}
      {!loading && logs.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No orders have been moved between drivers yet.
        </Typography>
      )}
      <Stack spacing={1.5}>
        {logs.map((log) => (
          <Box key={log.deliveryReassignmentId}>
            <Typography variant="body2">
              Order #{log.orderNumber}: {log.fromDriverName || "Unassigned"} → {log.toDriverName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(log.createdAt).toLocaleString()}
              {log.reason ? ` · ${log.reason}` : ""}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
